import { usePrevious } from '../../hooks'
import React, { ForwardedRef, forwardRef } from 'react'
import { useEffect } from 'react'
import ReactGA from 'react-ga4'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { ApplicationState } from '../../store'
import { getTitleBasedOnDomain } from '../../utils'
import css from './Document.module.scss'

interface DocumentProps {
	title: string
	description?: string
	keywords?: string
	children: React.ReactNode
	disableOverflow?: boolean
	stableScrollBarGutter?: boolean
	style?: React.CSSProperties
	className?: string
	srcs?: string[]
}

/**
 * Page wrapper providing:
 * - `<Helmet>` document metadata (title/description/keywords)
 * - optional `<script type="module">` tags via `srcs`
 * - consistent container styling/layout options
 * - GA pageview tracking on route changes
 */
// eslint-disable-next-line react/display-name
export const Document = forwardRef(
	(
		{ srcs = [], className, children, disableOverflow, title, description = title, keywords = title, stableScrollBarGutter, style }: DocumentProps,
		ref: ForwardedRef<HTMLDivElement> | undefined
	): React.ReactElement | null => {
		const location = useLocation()
		const searchParams = new URLSearchParams(location.search)

		// The path on last render
		const { pathname } = location
		const previousPathName = usePrevious(pathname)
		const isActivity = searchParams.get('activityId') !== null

		useEffect(() => {
			// If the current page doesn't equal the previous page then log Page View with GA irregardless of logged in state
			if (pathname !== previousPathName) {
				ReactGA.send({ hitType: 'pageview', page: pathname, title: `LynxCloud - ${getTitleBasedOnDomain(title, 'LYNX Cloud')}` })
			}
		}, [pathname, previousPathName])

		const classNames = [css['container']]
		disableOverflow && classNames.push(css['container--disable-overflow'])
		stableScrollBarGutter && classNames.push(css['container--enable-gutter'])
		isActivity && classNames.push(css['container--max'])
		className && classNames.push(className)

		const scripts = srcs.map((s) => <script key={s} src={s} type="module" async defer />)

		return (
			<React.Fragment>
				<Helmet>
					<meta charSet="utf-8" />
					<title>{getTitleBasedOnDomain(title, 'LYNX Cloud')}</title>
					<meta name="description" content={description || title} />
					<meta name="keywords" content={keywords || title} />
					{scripts}
				</Helmet>
				<div className={classNames.join(' ')} ref={ref} style={style}>
					{children}
				</div>
			</React.Fragment>
		)
	}
)
